const recommendationCache = new Map();

function routesRoot() {
  const root = window.Shopify && window.Shopify.routes && window.Shopify.routes.root;
  return typeof root === 'string' && root ? root : '/';
}

function requestComplementaryProducts(productId) {
  const key = String(productId);
  if (recommendationCache.has(key)) return recommendationCache.get(key);

  const request = (async () => {
    const endpoint = new URL(routesRoot() + 'recommendations/products.json', window.location.origin);
    endpoint.searchParams.set('product_id', key);
    endpoint.searchParams.set('limit', '4');
    endpoint.searchParams.set('intent', 'complementary');

    const response = await fetch(endpoint, {
      credentials: 'same-origin',
      headers: { Accept: 'application/json' },
    });
    if (!response.ok) return [];

    const payload = await response.json();
    return Array.isArray(payload.products) ? payload.products : [];
  })().catch(() => []);

  recommendationCache.set(key, request);
  return request;
}

function productImage(product) {
  const featured = product && product.featured_image;
  if (typeof featured === 'string') return featured;
  if (featured && typeof featured.src === 'string') return featured.src;
  if (Array.isArray(product && product.images) && typeof product.images[0] === 'string') return product.images[0];
  return '';
}

function sizedImageUrl(source) {
  if (!source) return '';
  try {
    const url = new URL(source, window.location.origin);
    url.searchParams.set('width', '160');
    return url.toString();
  } catch {
    return source;
  }
}

class KttCartComplementary extends HTMLElement {
  static get observedAttributes() {
    return ['data-product-ids', 'data-currency'];
  }

  constructor() {
    super();
    this.products = [];
    this.loadRevision = 0;
    this.startHandle = null;
    this.startHandleType = null;
  }

  connectedCallback() {
    this.scheduleLoad();
  }

  disconnectedCallback() {
    this.loadRevision += 1;
    this.cancelScheduledLoad();
  }

  attributeChangedCallback(_name, oldValue, newValue) {
    if (oldValue !== newValue && this.isConnected) this.scheduleLoad();
  }

  cancelScheduledLoad() {
    if (this.startHandle === null) return;

    if (this.startHandleType === 'idle' && 'cancelIdleCallback' in window) {
      window.cancelIdleCallback(this.startHandle);
    } else {
      window.clearTimeout(this.startHandle);
    }

    this.startHandle = null;
    this.startHandleType = null;
  }

  scheduleLoad() {
    this.loadRevision += 1;
    const revision = this.loadRevision;

    this.cancelScheduledLoad();
    this.products = [];
    this.hidden = true;

    const list = this.querySelector('[data-ktt-recommendation-list]');
    if (list) list.replaceChildren();

    const start = () => {
      this.startHandle = null;
      this.startHandleType = null;
      this.load(revision);
    };

    if ('requestIdleCallback' in window) {
      this.startHandleType = 'idle';
      this.startHandle = window.requestIdleCallback(start, { timeout: 600 });
    } else {
      this.startHandleType = 'timeout';
      this.startHandle = window.setTimeout(start, 0);
    }
  }

  async load(revision) {
    if (revision !== this.loadRevision || !this.isConnected) return;

    const sourceIds = (this.dataset.productIds || '')
      .split(',')
      .map((id) => id.trim())
      .filter(Boolean);
    if (!sourceIds.length) return;

    const cartIds = new Set(sourceIds);
    const seen = new Set();
    const products = [];

    for (const sourceId of sourceIds) {
      const candidates = await requestComplementaryProducts(sourceId);
      if (revision !== this.loadRevision || !this.isConnected) return;

      for (const product of candidates) {
        const id = String(product && product.id ? product.id : '');
        if (!id || cartIds.has(id) || seen.has(id) || product.available === false) continue;
        seen.add(id);
        products.push(product);
        if (products.length === 3) break;
      }
      if (products.length === 3) break;
    }

    if (revision !== this.loadRevision || !this.isConnected) return;

    this.products = products;
    this.renderProducts(products, revision);
  }

  renderProducts(products, revision) {
    if (revision !== this.loadRevision || !this.isConnected) return;

    const list = this.querySelector('[data-ktt-recommendation-list]');
    if (!list) return;
    list.replaceChildren();

    const currency = this.dataset.currency || 'USD';
    const formatter = new Intl.NumberFormat(document.documentElement.lang || 'en-US', {
      style: 'currency',
      currency,
    });

    const fragment = document.createDocumentFragment();
    for (const product of products) {
      const title = typeof product.title === 'string' ? product.title.trim() : '';
      const handle = typeof product.handle === 'string' ? product.handle.trim() : '';
      if (!title || !handle) continue;

      const item = document.createElement('li');
      const link = document.createElement('a');
      link.className = 'ktt-cart-complementary__link';
      link.href = typeof product.url === 'string' && product.url
        ? product.url
        : routesRoot() + 'products/' + encodeURIComponent(handle);
      link.setAttribute('aria-label', 'View ' + title);

      const imageSource = sizedImageUrl(productImage(product));
      if (imageSource) {
        const image = document.createElement('img');
        image.className = 'ktt-cart-complementary__image';
        image.src = imageSource;
        image.alt = title;
        image.width = 72;
        image.height = 72;
        image.loading = 'lazy';
        image.decoding = 'async';
        link.appendChild(image);
      } else {
        const placeholder = document.createElement('span');
        placeholder.className = 'ktt-cart-complementary__image-placeholder';
        placeholder.setAttribute('aria-hidden', 'true');
        link.appendChild(placeholder);
      }

      const copy = document.createElement('span');
      copy.className = 'ktt-cart-complementary__copy';

      const titleNode = document.createElement('span');
      titleNode.className = 'ktt-cart-complementary__title';
      titleNode.textContent = title;
      copy.appendChild(titleNode);

      const priceValue = Number(product.price_min ?? product.price);
      if (Number.isFinite(priceValue)) {
        const price = document.createElement('span');
        price.className = 'ktt-cart-complementary__price';
        price.textContent = formatter.format(priceValue / 100);
        copy.appendChild(price);
      }

      link.appendChild(copy);
      item.appendChild(link);
      fragment.appendChild(item);
    }

    if (!fragment.childNodes.length) {
      this.hidden = true;
      return;
    }

    list.appendChild(fragment);
    this.hidden = false;
  }
}

if (!customElements.get('ktt-cart-complementary')) {
  customElements.define('ktt-cart-complementary', KttCartComplementary);
}
